import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Delete } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import {
  classifyFlowgateResultError,
  useCreateFlowgateSelectionComment,
  useDeleteFlowgateSelectionComment,
  useFlowgateSelection,
  useFlowgateSelectionComments,
  type FlowgateCommentType,
  type FlowgateValidationStatus,
} from '@/api/flowgate';

const STATUS_LABEL: Record<FlowgateValidationStatus, string> = {
  pending: 'Pending',
  first_validated: '1st validated',
  second_validated: '2nd validated',
  rejected: 'Rejected',
};

const STATUS_COLOR: Record<
  FlowgateValidationStatus,
  'default' | 'info' | 'success' | 'error'
> = {
  pending: 'default',
  first_validated: 'info',
  second_validated: 'success',
  rejected: 'error',
};

const COMMENT_TYPES: { value: FlowgateCommentType; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'first_validation', label: 'First validation' },
  { value: 'second_validation', label: 'Second validation' },
  { value: 'rejection', label: 'Rejection' },
];

const COMMENT_TYPE_LABEL: Record<FlowgateCommentType, string> = {
  general: 'General',
  first_validation: 'First validation',
  second_validation: 'Second validation',
  rejection: 'Rejection',
};

type Props = {
  selectionId: string;
  resultId?: string;
  onClose: () => void;
};

const FlowgateSelectionDrawer = ({ selectionId, resultId, onClose }: Props) => {
  const { data: selection } = useFlowgateSelection(selectionId);
  const { data: comments, isLoading: commentsLoading } =
    useFlowgateSelectionComments(selectionId);
  const createComment = useCreateFlowgateSelectionComment();
  const deleteComment = useDeleteFlowgateSelectionComment();

  const [commentType, setCommentType] =
    useState<FlowgateCommentType>('general');
  const [commentText, setCommentText] = useState('');

  const surfaceError = (err: unknown) => {
    const info = classifyFlowgateResultError(err);
    enqueueSnackbar(info.message, {
      variant: info.kind === 'not_ready' ? 'warning' : 'error',
    });
  };

  const handleSend = () => {
    const text = commentText.trim();
    if (!text) return;
    createComment.mutate(
      {
        selectionId,
        resultId,
        body: { comment_type: commentType, comment: text },
      },
      {
        onSuccess: () => {
          setCommentText('');
          setCommentType('general');
        },
        onError: surfaceError,
      },
    );
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 480 },
          bgcolor: 'background.paper',
        },
      }}
    >
      <Stack
        sx={{ height: '100%', overflowY: 'auto', p: { xs: 2, sm: 2.5 } }}
        spacing={2}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h3">Variant review</Typography>
            <Typography variant="body2" color="text.secondary">
              Row {selection?.row_id ?? '—'}
            </Typography>
            {selection?.created_by_email && (
              <Typography variant="caption" color="text.secondary">
                Selected by {selection.created_by_email}
              </Typography>
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            {selection && (
              <Chip
                size="small"
                label={STATUS_LABEL[selection.validation_status]}
                color={STATUS_COLOR[selection.validation_status]}
              />
            )}
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        <Box>
          <Typography variant="subtitle1">Comments</Typography>
          <Typography variant="body2" color="text.secondary">
            Document review context and validation decisions.
          </Typography>
        </Box>
        {commentsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!commentsLoading && (!comments || comments.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No comments yet.
          </Typography>
        )}
        {!commentsLoading && comments && comments.length > 0 && (
          <List dense disablePadding>
            {comments.map((c) => (
              <ListItem
                key={c.id}
                alignItems="flex-start"
                sx={{
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                }}
                secondaryAction={
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() =>
                      deleteComment.mutate(
                        {
                          selectionId,
                          commentId: c.id,
                          resultId,
                        },
                        { onError: surfaceError },
                      )
                    }
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        label={COMMENT_TYPE_LABEL[c.comment_type]}
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {c.created_by_email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(c.created_at).toLocaleString()}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                    >
                      {c.comment}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider />

        <Stack
          spacing={1}
          sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1.5 }}
        >
          <Typography variant="subtitle2">Add review comment</Typography>
          <Select
            size="small"
            value={commentType}
            onChange={(e) =>
              setCommentType(e.target.value as FlowgateCommentType)
            }
          >
            {COMMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            multiline
            minRows={3}
            placeholder="Write a comment..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!commentText.trim() || createComment.isPending}
          >
            Send
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default FlowgateSelectionDrawer;
